import React, { useState } from 'react';
import {
  UtilityMeter,
  UtilityMeterBillingMode,
  UtilityMeterStatus,
  UpdateUtilityMeterData,
  utilityMetersApi,
} from '../../api/utilityMetersApi';
import { getQuotaTierLabel } from '../../utils/quotaTierLabels';

interface UtilityMeterEditModalProps {
  meter: UtilityMeter | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: (meter: UtilityMeter) => void;
  t: (key: string) => string;
  quotaTierOptions?: number[];
}

export const UtilityMeterEditModal: React.FC<UtilityMeterEditModalProps> = ({
  meter,
  isOpen,
  onClose,
  onSaved,
  t,
  quotaTierOptions = [1, 2, 3, 4],
}) => {
  const [form, setForm] = useState<UpdateUtilityMeterData>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (meter) {
      setForm({
        meterCode: meter.meterCode,
        qrCode: meter.qrCode ?? '',
        billingMode: meter.billingMode,
        feeMultiplier: meter.feeMultiplier ?? undefined,
        quotaTierLevel: meter.quotaTierLevel ?? 1,
        registrationDate: meter.registrationDate
          ? new Date(meter.registrationDate).toISOString().slice(0, 10)
          : '',
        initialReading: meter.initialReading,
        status: meter.status,
        notes: meter.notes ?? '',
      });
      setError(null);
    }
  }, [meter]);

  if (!isOpen || !meter) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      const payload: UpdateUtilityMeterData = {
        ...form,
        feeMultiplier:
          form.feeMultiplier === undefined || form.feeMultiplier === ('' as unknown as number)
            ? null
            : Number(form.feeMultiplier),
        quotaTierLevel: form.quotaTierLevel ?? 1,
        qrCode: form.qrCode || undefined,
        notes: form.notes || undefined,
      };
      const updated = await utilityMetersApi.update(meter.id, payload);
      onSaved({ ...meter, ...updated });
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t('failedToSaveMeter'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">
            {t('editMeterTitle')} — {meter.meterCode}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {t('apartmentCode')}: {meter.apartmentCode}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="text-red-600 text-sm bg-red-50 p-2 rounded">{error}</div>}
          <div>
            <label className="block text-sm font-medium mb-1">{t('meterCode')}</label>
            <input
              className="w-full border rounded px-3 py-2"
              value={form.meterCode ?? ''}
              onChange={(e) => setForm({ ...form, meterCode: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">QR</label>
            <input
              className="w-full border rounded px-3 py-2"
              value={form.qrCode ?? ''}
              onChange={(e) => setForm({ ...form, qrCode: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('billingMode')}</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={form.billingMode ?? 'NON_QUOTA'}
              onChange={(e) =>
                setForm({ ...form, billingMode: e.target.value as UtilityMeterBillingMode })
              }
            >
              <option value="QUOTA">{t('billingModeQuota')}</option>
              <option value="NON_QUOTA">{t('billingModeNonQuota')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('feeMultiplier')}</label>
            <input
              type="number"
              min={0}
              className="w-full border rounded px-3 py-2"
              value={form.feeMultiplier ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  feeMultiplier: e.target.value === '' ? undefined : parseInt(e.target.value, 10),
                })
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('quotaTierStart')}</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={String(form.quotaTierLevel ?? 1)}
              onChange={(e) =>
                setForm({ ...form, quotaTierLevel: parseInt(e.target.value, 10) || 1 })
              }
            >
              {quotaTierOptions.map((level) => (
                <option key={level} value={level}>
                  {getQuotaTierLabel(level, t)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('registrationDate')}</label>
            <input
              type="date"
              className="w-full border rounded px-3 py-2"
              value={form.registrationDate ?? ''}
              onChange={(e) => setForm({ ...form, registrationDate: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('initialReading')}</label>
            <input
              type="number"
              min={0}
              step="any"
              className="w-full border rounded px-3 py-2"
              value={form.initialReading ?? 0}
              onChange={(e) =>
                setForm({ ...form, initialReading: parseFloat(e.target.value) || 0 })
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('status')}</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={form.status ?? 'ACTIVE'}
              onChange={(e) =>
                setForm({ ...form, status: e.target.value as UtilityMeterStatus })
              }
            >
              <option value="ACTIVE">{t('meterStatusActive')}</option>
              <option value="INACTIVE">{t('meterStatusInactive')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('notes')}</label>
            <textarea
              className="w-full border rounded px-3 py-2"
              rows={2}
              value={form.notes ?? ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded hover:bg-gray-50">
              {t('cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? t('saving') : t('save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
